import { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import api from "../../../Services/api";
import { usePentagovContext } from "../../../context/pentagovContext";
import { useOperador } from "../../../Hooks/useOperador";
import Swal from "sweetalert2";
import { MdModeEdit, MdPlaylistAdd } from "react-icons/md";
import "./cadastroSecaoModal.css";

const CadastroSecaoModal = ({
    open,
    onClose,
    IDSecao,
    IDCliente,
    totalSecoes = 0,
    onSaved,
    clienteNome,
    clienteUf,
}) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [opcoesSecaoPai, setOpcoesSecaoPai] = useState([]);
    const [isGrupoSecoes, setIsGrupoSecoes] = useState(false);
    const [secaoMarcadaComoGrupo, setSecaoMarcadaComoGrupo] = useState(false);
    const [associarAGrupo, setAssociarAGrupo] = useState(false);
    const [bloquearToggleGrupoSecoes, setBloquearToggleGrupoSecoes] = useState(false);

    const { IDProcessoAberto } = usePentagovContext();
    const { IDOperador } = useOperador();

    const [form, setForm] = useState({
        IDSecao: 0,
        IDCliente: IDCliente,
        IDSecaoPai: "",
        descricao: "",
        ordem: 1,
        situacao: 1,
        dataCadastro: "",
        dataAlteracao: "",
    });

    const calcularProximaOrdem = (lista = []) => {
        const usadas = new Set();
        let max = 0;
        lista.forEach((item) => {
            const ordem = Number(item?.ordem);
            if (Number.isFinite(ordem) && ordem > 0) {
                usadas.add(ordem);
                if (ordem > max) max = ordem;
            }
        });
        for (let i = 1; i <= max + 1; i += 1) {
            if (!usadas.has(i)) return i;
        }
        return max + 1;
    };

    // Carregar dados ao abrir o modal
    useEffect(() => {
        if (!open) return;

        const carregar = async () => {
            if (!IDSecao) {
                setLoading(true);
                setForm({
                    IDSecao: 0,
                    IDCliente: IDCliente,
                    IDSecaoPai: "",
                    descricao: "",
                    ordem: 1,
                    situacao: 1,
                    dataCadastro: "",
                    dataAlteracao: "",
                });
                setIsGrupoSecoes(false);
                setSecaoMarcadaComoGrupo(false);
                setAssociarAGrupo(false);
                setBloquearToggleGrupoSecoes(false);
                try {
                    if (IDCliente) {
                        const response = await api.get("/chksecoesstats", {
                            params: {
                                page: 1,
                                limit: totalSecoes || 9999,
                                IDCliente,
                                filter: "",
                            },
                        });
                        const lista = response.data?.data ?? [];
                        setOpcoesSecaoPai(
                            lista.filter((s) => Number(s.qntQuestoes || 0) === 0)
                        );
                        const proximaOrdem = calcularProximaOrdem(lista);
                        setForm((prev) => ({ ...prev, ordem: proximaOrdem }));
                    }
                } catch (err) {
                    console.error("Erro ao buscar próxima ordem", err);
                } finally {
                    setLoading(false);
                }
                return;
            }

            try {
                const resp = await api.get(`/chksecao/${IDSecao}`);
                let listaSecoes = [];

                if (resp.data?.IDCliente) {
                    const responseSecoes = await api.get("/chksecoesstats", {
                        params: {
                            page: 1,
                            limit: totalSecoes || 9999,
                            IDCliente: resp.data.IDCliente,
                            filter: "",
                        },
                    });
                    listaSecoes = responseSecoes.data?.data ?? [];
                }
                const secaoAtual = listaSecoes.find(
                    (s) => Number(s.IDSecao) === Number(IDSecao)
                );
                const qntSubSecoesAtual = Number(secaoAtual?.qntSubSecoes || 0);
                const possuiQuestoes = Number(secaoAtual?.qntQuestoes || 0) > 0;

                setIsGrupoSecoes(qntSubSecoesAtual > 0);
                setSecaoMarcadaComoGrupo(qntSubSecoesAtual > 0);
                setBloquearToggleGrupoSecoes(possuiQuestoes);
                setAssociarAGrupo(
                    qntSubSecoesAtual === 0 && Boolean(resp.data.IDSecaoPai)
                );

                setOpcoesSecaoPai(
                    listaSecoes.filter(
                        (s) =>
                            Number(s.IDSecao) !== Number(IDSecao) &&
                            Number(s.qntQuestoes || 0) === 0
                    )
                );

                setForm({
                    IDSecao: resp.data.IDSecao,
                    IDCliente: resp.data.IDCliente,
                    IDSecaoPai: resp.data.IDSecaoPai ?? "",
                    descricao: resp.data.descricao,
                    ordem: resp.data.ordem ?? 1,
                    situacao: resp.data.situacao ?? 1,
                    dataCadastro: resp.data.dataCadastro,
                    dataAlteracao: resp.data.dataAlteracao
                });

            } catch (err) {
                console.error(err);
                Swal.fire("Erro", "Erro ao carregar a seção.", "error");
            }

            setLoading(false);
        };

        carregar();
    }, [IDSecao, open, IDCliente, totalSecoes]);

    const buscaDadosDiretorioAnexos = async () => {
        try {
            const { data } = await api.get("/diretorios", {
                params: {
                    filter: "Anexos-Auditoria",
                    page: 1,
                    limit: 1000,
                },
            });

            const diretorioAnexos = (data || []).find(
                (diretorio) =>
                    Number(diretorio.IDProcesso) === Number(IDProcessoAberto) &&
                    diretorio.descricao === "Anexos-Auditoria"
            );

            return diretorioAnexos || null;
        } catch (err) {
            console.error("Erro ao buscar diretório Anexos-Auditoria:", err);
            return null;
        }
    };

    // Atualizar inputs
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // Salvar
    const salvarSecao = async (e) => {
        e.preventDefault();
        setSaving(true);

        const payload = {
            IDSecao: IDSecao ?? form.IDSecao,
            IDCliente: form.IDCliente,
            IDSecaoPai:
                !secaoMarcadaComoGrupo && associarAGrupo && form.IDSecaoPai
                    ? Number(form.IDSecaoPai)
                    : null,
            descricao: form.descricao.trim(),
            ordem: form.ordem ? Number(form.ordem) : null,
            situacao: form.situacao ? Number(form.situacao) : 1
        };

        try {
            if (IDSecao) {
                await api.put(`/chksecao/${IDSecao}`, payload);

                await Swal.fire({
                    icon: "success",
                    title: "Atualizado!",
                    text: "Seção atualizada com sucesso.",
                });
            } else {
                await api.post(`/chksecao`, payload);
                const diretorioAnexos = await buscaDadosDiretorioAnexos();

                if (diretorioAnexos) {
                    //Cria diretorio para anexos de Auditoria
                    await api.post("/diretorio", {
                        IDProcesso: IDProcessoAberto,
                        IDCriador: IDOperador,
                        descricao: payload.descricao,
                        descricaoExibicao: payload.descricao,
                        IDPai: diretorioAnexos.IDDiretorio,
                        IDFilho: 0,
                        nomeBucketAws: diretorioAnexos.nomeBucketAws ?? null,
                        tipo: "1",
                        situacao: 1,
                    });
                }

                await Swal.fire({
                    icon: "success",
                    title: "Cadastrado!",
                    text: "Seção cadastrada com sucesso.",
                });
            }

            onSaved(); // sinaliza para recarregar a lista
            onClose(); // fecha o modal

        } catch (err) {
            console.error(err);
            Swal.fire("Erro", "Erro ao salvar os dados.", "error");
        }

        setSaving(false);
    };

    if (!open) return null;

    return (
        <div className="cadSec-modal-overlay">
            <div className="cadSec-modal">

                <h2 className="cadSec-title">
                    <span className="cadSec-title-icon">
                        {IDSecao ? <MdModeEdit /> : <MdPlaylistAdd />}
                    </span>
                    {IDSecao ? "Editar Seção" : "Cadastrar Nova Seção"}
                </h2>

                {loading ? (
                    <div className="cadSec-loading">Carregando...</div>
                ) : (
                    <form className="cadSec-form" onSubmit={salvarSecao}>

                        {/* Linha 1 */}
                        <div className="cadSec-row">

                            {IDSecao && (
                                <div className="cadSec-field">
                                    <label>ID Seção</label>
                                    <input type="text" value={form.IDSecao} disabled />
                                </div>
                            )}

                            <div className="cadSec-field">
                                <label>Cliente</label>
                                <input
                                    type="text"
                                    value={
                                        clienteNome
                                            ? `${clienteNome}${clienteUf ? ` - ${clienteUf}` : ""}`
                                            : `ID ${form.IDCliente}`
                                    }
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Linha 2 */}
                        <div className="cadSec-field">
                            <label>Descrição</label>
                            <input
                                type="text"
                                name="descricao"
                                maxLength={150}
                                minLength={3}
                                value={form.descricao}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="cadSec-row">
                            <div className="cadSec-field">
                                <label>Grupo seções <span style={{ fontSize: 10, paddingLeft: 5 }}>(Opcional)</span></label>
                                <Form.Check
                                    type="switch"
                                    id="switch-grupo-secoes"
                                    label={secaoMarcadaComoGrupo ? "Sim" : "Não"}
                                    checked={secaoMarcadaComoGrupo}
                                    disabled={bloquearToggleGrupoSecoes}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setSecaoMarcadaComoGrupo(checked);
                                        if (checked) {
                                            setAssociarAGrupo(false);
                                            setForm((prev) => ({ ...prev, IDSecaoPai: "" }));
                                        }
                                    }}
                                    className="switchGrupoSecoes"
                                />
                            </div>

                            <div className="cadSec-field">
                                <label>Associar à grupo <span style={{ fontSize: 10, paddingLeft: 5 }}>(Opcional)</span></label>
                                <Form.Check
                                    type="switch"
                                    id="switch-associar-grupo"
                                    label={associarAGrupo ? "Sim" : "Não"}
                                    checked={associarAGrupo}
                                    disabled={secaoMarcadaComoGrupo}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setAssociarAGrupo(checked);
                                        if (!checked) {
                                            setForm((prev) => ({ ...prev, IDSecaoPai: "" }));
                                        }
                                    }}
                                    className="switchGrupoSecoes"
                                />
                                {associarAGrupo && (
                                    <select
                                        name="IDSecaoPai"
                                        value={form.IDSecaoPai ?? ""}
                                        onChange={handleChange}
                                        disabled={secaoMarcadaComoGrupo}
                                    >
                                        <option value="">Selecione um grupo de seções</option>
                                        {opcoesSecaoPai.map((secao) => (
                                            <option key={secao.IDSecao} value={secao.IDSecao}>
                                                {secao.ordem ? `${secao.ordem} - ` : ""}{secao.descricao}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>

                        {/* Linha 3 */}
                        <div className="cadSec-row">
                            <div className="cadSec-field cadSec-field-ordem">
                                <label>Ordem<span style={{ fontSize: 10, paddingLeft: 5 }}>(Opcional)</span></label>
                                <input
                                    type="number"
                                    name="ordem"
                                    min={1}
                                    step={1}
                                    value={form.ordem}
                                    onChange={handleChange}
                                    onBlur={() => setForm(prev => ({ ...prev, ordem: prev.ordem || 1 }))}
                                    placeholder="Opcional"
                                />
                            </div>

                            {IDSecao && (
                                <div className="cadSec-field cadSec-field-situacao">
                                    <label>Situacao</label>
                                    <select
                                        name="situacao"
                                        value={form.situacao ?? 1}
                                        onChange={handleChange}
                                        disabled={isGrupoSecoes}
                                    >
                                        <option value={2} disabled>Finalizado</option>
                                        <option value={1}>Ativo</option>
                                        <option value={0}>Desativado</option>
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Linha 4 - Datas */}
                        {IDSecao && (
                            <div className="cadSec-row">
                                <div className="cadSec-field">
                                    <label>Cadastrado em</label>
                                    <input type="text" value={new Date(form.dataCadastro).toLocaleString('pt-BR', {
                                        dateStyle: 'short',
                                        timeStyle: 'short'
                                    })} disabled />
                                </div>

                                <div className="cadSec-field">
                                    <label>Alterado em</label>
                                    <input type="text" value={new Date(form.dataAlteracao).toLocaleString('pt-BR', {
                                        dateStyle: 'short',
                                        timeStyle: 'short'
                                    })} disabled />
                                </div>
                            </div>
                        )}

                        <div className="cadSec-buttons">
                            <button type="button" className="cadSec-btn-cancel" onClick={onClose}>
                                Cancelar
                            </button>

                            <button type="submit" className="cadSec-btn-save" disabled={saving}>
                                {saving ? "Salvando..." : "Salvar"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CadastroSecaoModal;
